import PropTypes from "prop-types";
import { Plus, MoreHorizontal, Trash2, UserCog, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { formatJoinDate, formatRoleLabel } from "@/lib/teamErrors";
import { TEAM_INVITES_UI_ENABLED } from "@/lib/featureFlags";

const ROLE_BADGE = {
  owner: "bg-indigo-100 text-indigo-700",
  admin: "bg-violet-100 text-violet-700",
  member: "bg-emerald-100 text-emerald-700",
  viewer: "bg-slate-100 text-slate-700",
};

function MemberAvatar({ name, size = 36 }) {
  const parts = (name || "?").trim().split(/\s+/).filter(Boolean);
  const initials = parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : (name || "?").slice(0, 2).toUpperCase();
  return (
    <div
      style={{ width: size, height: size, fontSize: size * 0.38 }}
      className="rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white font-semibold flex items-center justify-center shrink-0"
    >
      {initials}
    </div>
  );
}

MemberAvatar.propTypes = {
  name: PropTypes.string,
  size: PropTypes.number,
};

function TeamMemberRow({ member, currentUserId, canManageMembers, onOpenRoleDialog, onRemove }) {
  const isSelf = member.user_id === currentUserId || member.is_current_user;
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-3 flex flex-col gap-3 sm:flex-row sm:items-center min-w-0"
      data-testid={`team-row-${member.user_id}`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <MemberAvatar name={member.display_name || member.email} />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-slate-900 truncate">
            {member.display_name || member.email}
            {isSelf ? <span className="ml-1 text-[10px] text-indigo-700">(you)</span> : null}
          </div>
          <div className="text-[11px] text-slate-500 truncate">
            {member.email} · joined {formatJoinDate(member.joined_at)}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between sm:justify-end gap-2 shrink-0">
        <Badge
          variant="secondary"
          className={cn("text-[10px] shrink-0", ROLE_BADGE[member.role] || ROLE_BADGE.member)}
        >
          {formatRoleLabel(member.role)}
        </Badge>
        {canManageMembers && !isSelf ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10 sm:h-8 sm:w-8 shrink-0" aria-label="Member actions">
                <MoreHorizontal size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onOpenRoleDialog(member)}>
                <UserCog size={14} className="mr-2" /> Change role
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-rose-600 focus:text-rose-700"
                onClick={() => onRemove(member)}
              >
                <Trash2 size={14} className="mr-2" /> Remove member
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </div>
  );
}

TeamMemberRow.propTypes = {
  member: PropTypes.object.isRequired,
  currentUserId: PropTypes.string,
  canManageMembers: PropTypes.bool.isRequired,
  onOpenRoleDialog: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

function PendingInviteRow({ invite, canInvite, revokingId, onRevoke }) {
  return (
    <div
      className="rounded-lg border border-dashed border-slate-300 bg-slate-50/60 p-3 flex flex-col gap-3 sm:flex-row sm:items-center min-w-0"
      data-testid={`team-pending-${invite.invite_id}`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="h-9 w-9 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center shrink-0">
          <Plus size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-semibold text-slate-700 truncate">{invite.email}</div>
          <div className="text-[11px] text-slate-500">
            {formatRoleLabel(invite.role)} · expires {formatJoinDate(invite.expires_at)}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between sm:justify-end gap-2 shrink-0">
        <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-700 shrink-0">
          Pending
        </Badge>
        {canInvite ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 sm:h-8 sm:w-8 shrink-0 text-slate-500 hover:text-rose-600"
            disabled={revokingId === invite.invite_id}
            onClick={() => onRevoke(invite.invite_id)}
            aria-label="Revoke invite"
            data-testid={`revoke-invite-${invite.invite_id}`}
          >
            <X size={14} />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

PendingInviteRow.propTypes = {
  invite: PropTypes.object.isRequired,
  canInvite: PropTypes.bool.isRequired,
  revokingId: PropTypes.string,
  onRevoke: PropTypes.func.isRequired,
};

function renderTeamMembersBody({ loading, members, currentUserId, canManageMembers, onOpenRoleDialog, onRemove }) {
  if (loading) {
    return <div className="text-[12px] text-slate-500 px-1">Loading team…</div>;
  }
  if (members.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 p-4 text-[12.5px] text-slate-600">
        No members found for this workspace.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {members.map((m) => (
        <TeamMemberRow
          key={m.user_id}
          member={m}
          currentUserId={currentUserId}
          canManageMembers={canManageMembers}
          onOpenRoleDialog={onOpenRoleDialog}
          onRemove={onRemove}
        />
      ))}
    </div>
  );
}

export function TeamMembersPanel({
  members,
  loading,
  workspaceLabel,
  inviteDisabledReason,
  currentUserId,
  canManageMembers,
  onOpenRoleDialog,
  onRemove,
  onInviteClick,
}) {
  return (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-slate-900">Members</div>
          <div className="text-[11.5px] text-slate-500 mt-0.5">
            {workspaceLabel} · {members.length} member{members.length === 1 ? "" : "s"}
          </div>
        </div>
        {TEAM_INVITES_UI_ENABLED ? (
          <Button
            size="sm"
            className="h-10 sm:h-8 w-full sm:w-auto shrink-0"
            onClick={onInviteClick}
            disabled={!!inviteDisabledReason}
            data-testid="invite-team"
          >
            <Plus size={12} /> Invite teammate
          </Button>
        ) : null}
      </div>

      {TEAM_INVITES_UI_ENABLED && inviteDisabledReason ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
          {inviteDisabledReason}
        </div>
      ) : null}

      {renderTeamMembersBody({
        loading,
        members,
        currentUserId,
        canManageMembers,
        onOpenRoleDialog,
        onRemove,
      })}
    </>
  );
}

TeamMembersPanel.propTypes = {
  members: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired,
  workspaceLabel: PropTypes.string.isRequired,
  inviteDisabledReason: PropTypes.string,
  currentUserId: PropTypes.string,
  canManageMembers: PropTypes.bool.isRequired,
  onOpenRoleDialog: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onInviteClick: PropTypes.func.isRequired,
};

export function TeamPendingInvitesPanel({
  pendingInvites,
  loading,
  canInvite,
  revokingId,
  onRevoke,
}) {
  if (!TEAM_INVITES_UI_ENABLED) return null;

  return (
    <>
      <div className="pt-2">
        <div className="text-[13px] font-semibold text-slate-900">Pending invites</div>
        <div className="text-[11.5px] text-slate-500 mt-0.5">
          Invitations expire after 7 days.
        </div>
      </div>

      {!loading && pendingInvites.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 p-4 text-[12.5px] text-slate-600">
          No pending invitations.
        </div>
      ) : (
        <div className="space-y-2">
          {pendingInvites.map((inv) => (
            <PendingInviteRow
              key={inv.invite_id}
              invite={inv}
              canInvite={canInvite}
              revokingId={revokingId}
              onRevoke={onRevoke}
            />
          ))}
        </div>
      )}
    </>
  );
}

TeamPendingInvitesPanel.propTypes = {
  pendingInvites: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired,
  canInvite: PropTypes.bool.isRequired,
  revokingId: PropTypes.string,
  onRevoke: PropTypes.func.isRequired,
};
